import { Component, inject, output, signal } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { PopoverDirective } from "@kno2/common/directives";
import { NotificationService } from "@kno2/common/services/notifications";
import { APP_CONFIG_TOKEN } from "@kno2/core/config";
import { SessionService } from "@kno2/core/session";
import { SupportService } from "@kno2/data-access/support";
import { FormSelectComponent } from "@kno2/shared/forms";
import { TOOLTIP_FEATURE_AREA, TOOLTIP_SUBMIT_A_TICKET } from "../support.constants";
import { SUPPORT_TICKET_AREAS, SUPPORT_TICKET_ISSUE_TEXT_PLACEHOLDER, SUPPORT_TICKET_SEVERITY } from "./support.submit-a-ticket.constants";

@Component({
    selector: "kno2-support-submit-a-ticket",
    imports: [ReactiveFormsModule, FormSelectComponent, PopoverDirective],
    templateUrl: "./support.submit-a-ticket.component.html"
})
export class SubmitATicketComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly fb = inject(FormBuilder);
    private readonly supportService = inject(SupportService);
    private readonly sessionService = inject(SessionService);
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly notificationService = inject(NotificationService);

    protected readonly expanded = this.route.snapshot.routeConfig.path.endsWith("contact");
    protected readonly isSubmitting = signal(false);
    protected readonly hasSubmitted = signal(false);
    protected readonly toggleCollapse = output<void>();
    protected readonly sectionTooltip = TOOLTIP_SUBMIT_A_TICKET;
    protected readonly featureAreaTooltip = TOOLTIP_FEATURE_AREA;
    protected readonly areaOptions = SUPPORT_TICKET_AREAS(this.appConfig.brandDisplayName).map((name) => [name, name]);
    protected readonly severityOptions = SUPPORT_TICKET_SEVERITY(this.appConfig.brandDisplayName).map((name) => [name, name]);
    protected readonly issueTextPlaceholder = SUPPORT_TICKET_ISSUE_TEXT_PLACEHOLDER;
    protected readonly form = this.fb.group({
        area: [undefined, Validators.required],
        severity: [undefined, Validators.required],
        issueText: [undefined, Validators.required]
    });

    public async onSubmit(): Promise<void> {
        try {
            this.hasSubmitted.set(true);

            if (this.form.invalid) return;

            this.isSubmitting.set(true);

            const { fullName, userName: emailAddress } = this.sessionService.getProfile();
            const payload = {
                ...this.form.value,
                fullName,
                emailAddress
            };
            await this.supportService.submitTicket(payload);
            this.notificationService.success("The support ticket was submitted successfully.");
            this.form.reset();
            this.hasSubmitted.set(false);
        } catch (response) {
            this.notificationService.error(response?.error?.message || "There was an error saving this support ticket.");
        } finally {
            this.isSubmitting.set(false);
        }
    }
}
